import { useCallback, useEffect, useRef } from "react";

const SWIPE_UP_THRESHOLD_PX = 50;

interface UseSwipeDismissOptions {
	onDismiss: () => void;
	isEnabled: boolean;
}

/**
 * Returns a callback ref that attaches non-passive touch listeners to detect
 * an upward swipe gesture. Calling preventDefault on touchmove blocks the
 * native scroll that otherwise moves the page behind the panel on iOS Safari.
 */
export function useSwipeDismiss({
	onDismiss,
	isEnabled,
}: UseSwipeDismissOptions): {
	callbackRef: (node: HTMLDivElement | null) => void;
} {
	const startYRef = useRef<number | null>(null);
	const currentYRef = useRef<number | null>(null);
	const onDismissRef = useRef(onDismiss);
	const elementRef = useRef<HTMLDivElement | null>(null);
	const listenersRef = useRef<{
		start: (e: TouchEvent) => void;
		move: (e: TouchEvent) => void;
		end: () => void;
	} | null>(null);

	useEffect(() => {
		onDismissRef.current = onDismiss;
	}, [onDismiss]);

	const detach = useCallback(() => {
		const el = elementRef.current;
		const listeners = listenersRef.current;
		if (!el || !listeners) return;

		el.removeEventListener("touchstart", listeners.start);
		el.removeEventListener("touchmove", listeners.move);
		el.removeEventListener("touchend", listeners.end);
		listenersRef.current = null;
	}, []);

	const attach = useCallback((el: HTMLDivElement) => {
		const handleTouchStart = (event: TouchEvent) => {
			startYRef.current = event.touches[0].clientY;
			currentYRef.current = event.touches[0].clientY;
		};

		const handleTouchMove = (event: TouchEvent) => {
			if (startYRef.current === null) return;
			event.preventDefault();
			currentYRef.current = event.touches[0].clientY;
		};

		const handleTouchEnd = () => {
			if (startYRef.current === null || currentYRef.current === null) {
				return;
			}

			const deltaY = startYRef.current - currentYRef.current;
			if (deltaY >= SWIPE_UP_THRESHOLD_PX) {
				onDismissRef.current();
			}

			startYRef.current = null;
			currentYRef.current = null;
		};

		listenersRef.current = {
			start: handleTouchStart,
			move: handleTouchMove,
			end: handleTouchEnd,
		};

		el.addEventListener("touchstart", handleTouchStart, { passive: true });
		el.addEventListener("touchmove", handleTouchMove, { passive: false });
		el.addEventListener("touchend", handleTouchEnd);
	}, []);

	useEffect(() => {
		const el = elementRef.current;
		if (!el) return;

		if (isEnabled) {
			detach();
			attach(el);
		} else {
			detach();
		}

		return detach;
	}, [isEnabled, attach, detach]);

	const callbackRef = useCallback(
		(node: HTMLDivElement | null) => {
			detach();
			elementRef.current = node;

			if (node && isEnabled) {
				attach(node);
			}
		},
		[isEnabled, attach, detach],
	);

	return { callbackRef };
}
